import { StatusBarStyleEnum } from "@/enums/global";
import { Capacitor } from "@capacitor/core";
import { StatusBar, Style } from "@capacitor/status-bar";

// Map the project-local StatusBarStyleEnum ("default" / "light" / "dark") to
// @capacitor/status-bar's Style enum ("DEFAULT" / "LIGHT" / "DARK"). Consumers
// of this module use StatusBarStyleEnum exclusively so the grep-ability rule
// holds — no call site touches @capacitor/status-bar directly.
const styleMap: Record<StatusBarStyleEnum, Style> = {
  [StatusBarStyleEnum.Default]: Style.Default,
  [StatusBarStyleEnum.Light]: Style.Light,
  [StatusBarStyleEnum.Dark]: Style.Dark,
};

/**
 * Set the status bar text style (Default / Light / Dark).
 * No-op when the StatusBar Capacitor plugin is not available (web, or
 * stale native build without the plugin synced). BR-110, BR-113.
 *
 * Called by appSettingsStore.setStatusBarStyle, which itself is called by
 * router.afterEach — see screen-map.md § Router Hook.
 */
export async function setStatusBarStyle(style: StatusBarStyleEnum): Promise<void> {
  if (!Capacitor.isPluginAvailable("StatusBar")) {
    return;
  }
  await StatusBar.setStyle({ style: styleMap[style] });
}

/**
 * Set the status bar background color (Android only — iOS ignores it).
 * Hex string with leading "#". No-op on web or when the plugin is missing.
 * Exported for future-wave use; Wave 2 does NOT call this (BR-027).
 */
export async function setStatusBarBackgroundColor(hex: string): Promise<void> {
  if (!Capacitor.isPluginAvailable("StatusBar")) {
    return;
  }
  if (Capacitor.getPlatform() !== "android") {
    return;
  }
  await StatusBar.setBackgroundColor({ color: hex });
}
